import { ChevronLeftIcon, ChevronRightIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { getPlaceholderValue, isPlaceholderSelected } from 'pages/Loan/Reports/types'
import { isPipelineHeaderEmailAll, PIPELINE_ACTIONS_TITLE } from 'pages/Pipeline/constants'
import { useEffect, useState } from 'react'
import { getUserConfiguration } from 'services'

import { moveHeaderColumn, removeHeaderColumn } from './columnOps'
import { HeaderRow, HeaderSelectionModal } from './HeaderSelectionModal'

export interface LoanPipelineHeaderConfigsProps {
  userId?: number
  autoSave?: boolean
  maxRows?: number
  values: HeaderRow[]
  configKey?: 'loanPipelineHeaders' | 'loanPipelineServicingHeaders' | 'loanPipelineFciHeaders'
  setLoading: Function
  onSave: (values: HeaderRow[]) => void
}

export const LoanPipelineHeaderConfigs = ({
  userId = 0,
  autoSave = false,
  maxRows,
  values,
  configKey = 'loanPipelineHeaders',
  setLoading,
  onSave,
}: LoanPipelineHeaderConfigsProps) => {
  const ensureMinRows = (rows: HeaderRow[]) => {
    if (rows.length > 0) return rows
    if (maxRows === 1) return [['']]
    return []
  }

  const [headers, setHeaders] = useState<HeaderRow[]>(ensureMinRows(values || []))
  const [selectedCell, setSelectedCell] = useState<{ rowIndex: number; cellIndex: number } | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const canAddRow = maxRows == null || headers.length < maxRows

  useEffect(() => {
    loadData()
  }, [values, userId, configKey, maxRows])

  const loadData = async () => {
    setLoading(true)

    if (values && values.length > 0) setHeaders(values)
    else if ((!values || values.length === 0) && userId) {
      const data = await getUserConfiguration(userId)
      setHeaders(ensureMinRows(data[configKey] || []))
    } else {
      setHeaders(ensureMinRows([]))
    }

    setLoading(false)
  }

  const onCellClick = (rowIndex: number, cellIndex: number) => {
    setSelectedCell({ rowIndex, cellIndex })
    setIsModalOpen(true)
  }

  const onClearCell = (rowIndex: number, cellIndex: number, e: React.MouseEvent) => {
    e.stopPropagation() // Prevent triggering onCellClick
    const newHeaders = cloneDeep(headers)
    newHeaders[rowIndex][cellIndex] = ''
    setHeaders(newHeaders)
    autoSave && onSave(newHeaders)
  }

  const onAddColumn = () => {
    const newHeaders = cloneDeep(headers)
    if (newHeaders.length === 0) {
      newHeaders.push([''])
    } else {
      newHeaders.forEach((row) => {
        row.push('')
      })
    }
    setHeaders(newHeaders)
    autoSave && onSave(newHeaders)
  }

  const onAddRow = () => {
    if (maxRows != null && headers.length >= maxRows) return
    const newHeaders = cloneDeep(headers)
    const columnCount = headers.length > 0 && headers[0] ? headers[0].length : 1
    const newRow: HeaderRow = new Array(columnCount).fill(null).map(() => '')
    newHeaders.push(newRow)
    setHeaders(newHeaders)
    autoSave && onSave(newHeaders)
  }

  const onMoveColumnLeft = (cellIndex: number) => {
    const newHeaders = moveHeaderColumn(headers, cellIndex, -1)
    if (newHeaders === headers) return
    setHeaders(newHeaders)
    autoSave && onSave(newHeaders)
  }

  const onMoveColumnRight = (cellIndex: number) => {
    const newHeaders = moveHeaderColumn(headers, cellIndex, 1)
    if (newHeaders === headers) return
    setHeaders(newHeaders)
    autoSave && onSave(newHeaders)
  }

  const onDeleteColumn = (cellIndex: number) => {
    const newHeaders = removeHeaderColumn(headers, cellIndex)
    if (newHeaders === headers) return
    setHeaders(newHeaders)
    autoSave && onSave(newHeaders)
  }

  const onDeleteRow = (rowIndex: number) => {
    if (headers.length <= 1) {
      return
    }
    const newHeaders = cloneDeep(headers)
    newHeaders.splice(rowIndex, 1)
    setHeaders(newHeaders)
    autoSave && onSave(newHeaders)
  }

  const onSelectPlaceholder = (value: string, title: string) => {
    if (title !== PIPELINE_ACTIONS_TITLE) {
      value = getPlaceholderValue(title, value)
    }

    if (selectedCell) {
      const newHeaders = cloneDeep(headers)
      newHeaders[selectedCell.rowIndex][selectedCell.cellIndex] = value
      setHeaders(newHeaders)
      autoSave && onSave(newHeaders)
      setIsModalOpen(false)
      setSelectedCell(null)
    }
  }

  const onItemsSubmit = (selectedParty: string, partyPlaceholders: string[]) => {
    if (!selectedParty || !selectedCell) return

    const newHeaders = cloneDeep(headers)
    const newPlaceholders = partyPlaceholders.map((key) => `${selectedParty}-${key}`)

    if (newPlaceholders.length > 0) {
      newHeaders[selectedCell.rowIndex][selectedCell.cellIndex] = newPlaceholders[0]

      const maxColumns = Math.max(
        ...newHeaders.map((row) => row.length),
        selectedCell.cellIndex + newPlaceholders.length,
      )

      newHeaders.forEach((row) => {
        while (row.length < maxColumns) {
          row.push('')
        }
      })

      for (let i = 1; i < newPlaceholders.length; i++) {
        const targetCellIndex = selectedCell.cellIndex + i
        if (targetCellIndex < newHeaders[selectedCell.rowIndex].length) {
          newHeaders[selectedCell.rowIndex][targetCellIndex] = newPlaceholders[i]
        }
      }
    }

    setHeaders(newHeaders)
    autoSave && onSave(newHeaders)
    setIsModalOpen(false)
    setSelectedCell(null)
  }

  const checkSelected = (v: string, title: string) => {
    if (!selectedCell) return false
    if (title === PIPELINE_ACTIONS_TITLE) {
      return isPipelineHeaderEmailAll(headers[selectedCell.rowIndex][selectedCell.cellIndex])
    }
    return isPlaceholderSelected(v, title, headers[selectedCell.rowIndex][selectedCell.cellIndex])
  }

  return (
    <div className="relative">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex gap-4">
          <button onClick={onAddColumn} className="text-shade-blue hover:underline cursor-pointer">
            Add Column
          </button>
          {canAddRow && (
            <button onClick={onAddRow} className="text-shade-blue hover:underline cursor-pointer">
              Add Row
            </button>
          )}
        </div>
        {!autoSave && (
          <button onClick={() => onSave(headers)} className="text-shade-blue hover:underline cursor-pointer">
            Save
          </button>
        )}
      </div>

      <div className="overflow-x-auto text-sm">
        <table className="w-full border-collapse min-w-full">
          <tbody>
            {headers.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, cellIndex) => (
                  <td
                    key={cellIndex}
                    className="border border-gray-300 p-2 cursor-pointer hover:bg-gray-100 min-w-[150px] whitespace-nowrap"
                    onClick={() => onCellClick(rowIndex, cellIndex)}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className={cell ? '' : 'text-gray-400 italic'}>{cell || 'Click to select'}</span>
                      {cell && (
                        <button
                          onClick={(e) => onClearCell(rowIndex, cellIndex, e)}
                          className="text-gray-400 hover:text-red-500 flex-shrink-0"
                          title="Clear cell"
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                ))}
                <td className="border border-gray-300 p-2">
                  {maxRows !== 1 && (
                    <button
                      onClick={() => onDeleteRow(rowIndex)}
                      className="text-red-500 hover:text-red-700"
                      title="Delete row"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            <tr>
              {headers[0]?.map((_, cellIndex) => (
                <td key={cellIndex} className="border border-gray-300 p-2">
                  <div className="flex items-center gap-1 justify-center">
                    <button
                      onClick={() => onMoveColumnLeft(cellIndex)}
                      disabled={cellIndex === 0}
                      className={`${
                        cellIndex === 0
                          ? 'text-gray-300 cursor-not-allowed'
                          : 'text-shade-blue hover:text-shade-blue-dark'
                      } p-1`}
                      title="Move column left"
                    >
                      <ChevronLeftIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onMoveColumnRight(cellIndex)}
                      disabled={cellIndex >= (headers[0]?.length || 0) - 1}
                      className={`${
                        cellIndex >= (headers[0]?.length || 0) - 1
                          ? 'text-gray-300 cursor-not-allowed'
                          : 'text-shade-blue hover:text-shade-blue-dark'
                      } p-1`}
                      title="Move column right"
                    >
                      <ChevronRightIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onDeleteColumn(cellIndex)}
                      className="text-red-500 hover:text-red-700 p-1"
                      title="Delete column"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      <HeaderSelectionModal
        isOpen={isModalOpen}
        headers={headers}
        selectedCell={selectedCell}
        onClose={() => {
          setIsModalOpen(false)
          setSelectedCell(null)
        }}
        onSelectPlaceholder={onSelectPlaceholder}
        onItemsSubmit={onItemsSubmit}
        checkSelected={checkSelected}
      />
    </div>
  )
}
